import React, { useEffect, useState } from 'react';
import { Mic, MicOff, Brain, Plus, Save, Key } from 'lucide-react';
import { useStore } from '../lib/store';
import SpeechRecognition, { useSpeechRecognition } from 'react-speech-recognition';
import { processTeaching } from '../lib/teachingService';

export const TranscriptionPanel = () => {
  const {
    recording,
    aiInteractionEnabled,
    setRecording,
    setAiInteractionEnabled,
    setCurrentTranscript,
    currentTeaching,
    addTeaching,
    hasValidApiKeys
  } = useStore();
  
  const [isAddingTranscript, setIsAddingTranscript] = useState(false);
  const [editableTranscript, setEditableTranscript] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const {
    transcript,
    listening,
    resetTranscript,
    browserSupportsSpeechRecognition,
  } = useSpeechRecognition({
    clearTranscriptOnListen: false,
  });

  useEffect(() => {
    return () => {
      if (recording) {
        SpeechRecognition.stopListening();
        setRecording(false);
      }
    };
  }, []);

  useEffect(() => {
    if (recording && transcript) {
      setEditableTranscript(transcript);
      setCurrentTranscript(transcript);
    }
  }, [transcript, recording]);

  const handleProcessing = async () => {
    if (!hasValidApiKeys()) {
      setError('Please configure your API keys first');
      return;
    }

    try {
      setError(null);
      const finalTranscript = isEditing ? editableTranscript : transcript;
      
      if (isAddingTranscript && currentTeaching) {
        const newTeaching = {
          ...currentTeaching,
          additionalTranscripts: [
            ...(currentTeaching.additionalTranscripts || []),
            {
              content: finalTranscript,
              timestamp: new Date()
            }
          ]
        };
        addTeaching(newTeaching);
      } else {
        await processTeaching(finalTranscript);
      }
      
      resetTranscript();
      setEditableTranscript('');
      setIsAddingTranscript(false);
      setIsEditing(false);
    } catch (error: any) {
      console.error('Error during processing:', error);
      setError(error.message || 'Error processing teaching');
    }
  };

  if (!browserSupportsSpeechRecognition) {
    return (
      <div className="p-4 bg-red-500/10 border border-red-500 rounded-lg text-red-500">
        Your browser doesn't support speech recognition. Please try using Chrome or Edge.
      </div>
    );
  }

  const toggleRecording = async () => {
    if (!recording) {
      await SpeechRecognition.startListening({ continuous: true });
      setRecording(true);
      setIsEditing(false);
    } else {
      SpeechRecognition.stopListening();
      setRecording(false);
      if (transcript) {
        handleProcessing();
      }
    }
  };

  const toggleAIInteraction = () => {
    setAiInteractionEnabled(!aiInteractionEnabled);
  };

  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setEditableTranscript(e.target.value);
    setIsEditing(true);
  };

  return (
    <div className="w-full max-w-4xl mx-auto p-6 rounded-lg bg-gradient-to-br from-gray-900 to-gray-800 shadow-xl">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <button
            onClick={toggleRecording}
            className={`p-4 rounded-full transition-all duration-200 ${
              recording 
                ? 'bg-red-500 hover:bg-red-600 animate-pulse' 
                : 'bg-amber-500 hover:bg-amber-600'
            }`}
          >
            {recording ? <MicOff className="w-6 h-6" /> : <Mic className="w-6 h-6" />}
          </button>
          <span className="text-gray-400">
            {recording ? 'Recording...' : 'Click to start'}
          </span>
        </div>
        
        <div className="flex items-center gap-4">
          {currentTeaching && (
            <button
              onClick={() => setIsAddingTranscript(true)}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-700 hover:bg-gray-600 transition-colors"
            >
              <Plus className="w-5 h-5" />
              Add Clarification
            </button>
          )}
          <label className="flex items-center gap-2 cursor-pointer">
            <Brain className={`w-6 h-6 transition-colors duration-200 ${aiInteractionEnabled ? 'text-amber-500' : 'text-gray-400'}`} />
            <div 
              className="relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2"
              onClick={toggleAIInteraction}
            >
              <span
                className={`${
                  aiInteractionEnabled ? 'bg-amber-500' : 'bg-gray-600'
                } inline-block h-6 w-11 rounded-full transition-colors duration-200`}
              >
                <span
                  className={`${
                    aiInteractionEnabled ? 'translate-x-6' : 'translate-x-1'
                  } inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200 mt-1`}
                />
              </span>
            </div>
            <span className="text-sm text-gray-400">
              {aiInteractionEnabled ? 'AI Enabled' : 'AI Disabled'}
            </span>
          </label>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-500/10 border border-red-500 rounded-lg text-red-500 flex items-center gap-2">
          <Key className="w-5 h-5" />
          {error}
        </div>
      )}

      <div className="relative min-h-[200px] max-h-[400px] overflow-y-auto rounded-lg bg-gray-950 text-gray-100">
        {recording && (
          <div className="absolute top-2 right-2">
            <div className="flex gap-1">
              <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
              <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse delay-100"></div>
              <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse delay-200"></div>
            </div>
          </div>
        )}
        <div className="p-4">
          {isAddingTranscript && (
            <div className="mb-2 text-amber-500 font-semibold">
              Recording Additional Clarification...
            </div>
          )}
          <textarea
            className="w-full min-h-[180px] bg-transparent border-none focus:ring-0 text-gray-100 resize-none"
            value={isEditing ? editableTranscript : transcript || ''}
            onChange={handleTextChange}
            placeholder="Start speaking or type here..."
            disabled={recording}
          />
        </div>
      </div>

      {isEditing && (
        <div className="mt-4 flex justify-end">
          <button
            onClick={handleProcessing}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-amber-500 hover:bg-amber-600 transition-colors text-gray-900"
          >
            <Save className="w-5 h-5" />
            Save Changes
          </button>
        </div>
      )}
    </div>
  );
};